<template>
  <div :class="rootClasses" :style="rootStyles">
    <ElementControls
      v-if="editing"
      :no-page-break="!pageBreakAllowed"
      @delete="$emit('delete')"
      @duplicate="$emit('duplicate')"
      @page-break="$emit('page-break')"
      @hide="$emit('hide')"
    />
    <div :class="layoutClasses">
      <div
        v-if="label || description"
        class="formester-label-description-wrapper"
        :style="labelDescriptionWrapperStyles"
      >
        <label
          :class="labelClasses"
          :style="labelStyles"
          :for="item.label"
          v-html="label"
        >
        </label>
        <SmartText
          :active="active"
          :editing="editing"
          :item="item"
          :style="descriptionStyles"
          :text="description"
        />
      </div>

      <div class="audio-wrapper">
        <template v-if="audio.url">
          <iframe
            v-if="
              audio.url &&
              (audio.type === 'spotify' || audio.type === 'youtube')
            "
            :src="audio.url"
            :style="audioStyle"
            frameBorder="0"
            allowfullscreen=""
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            sandbox="allow-scripts allow-popups allow-top-navigation-by-user-activation allow-forms allow-same-origin allow-presentation"
          />

          <audio v-else-if="audio.url" controls>
            <source :src="audio.url" :type="audio.audioType" />
            Your browser does not support the audio element.
          </audio>

          <div v-else class="d-flex flex-column align-items-center">
            <i class="fa fa-volume-up placeholder-image my-3" />
            <p style="text-align: center">
              We could not parse this audio link. You may want to try a generic
              embed component if this component is not working for you
            </p>
          </div>
        </template>

        <AudioIcon v-else color="#697586" class="placeholder-image" />
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from "lodash-es";
import ElementControls from "@/components/form_fields/shared/element-controls.vue";
import commonStyles from "@/components/form_fields/shared/commonStyles";
import SmartText from "@/components/form_fields/shared/smart-text.vue";
import sharedActions from "@/components/form_fields/shared/sharedActions";
import AudioIcon from "@/components/icons/audio.vue";
import { get } from "lodash";

export default {
  name: "FormAudio",
  components: {
    ElementControls,
    SmartText,
    AudioIcon,
  },
  mixins: [commonStyles, sharedActions],
  props: {
    item: {
      type: Object,
      required: true,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
    styling: {
      type: Object,
      default: () => {
        return {};
      },
    },
    pageBreakAllowed: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["delete", "page-break", "duplicate", "hide"],
  data() {
    return {
      audio: {},
      validateAndSetupAudioDebounced: null,
    };
  },
  computed: {
    link() {
      return get(this.item, "preview.image", "") || this.item.link;
    },
    audioStyle() {
      return {
        height: this.itemStyling.height
          ? `${this.itemStyling.height}px`
          : "fit-content",
        width: "100%",
        "border-radius": "12px",
      };
    },
  },
  watch: {
    link: {
      handler: function (newValue, oldValue) {
        if (newValue === oldValue) return;
        this.validateAndSetupAudioDebounced();
      },
    },
  },
  mounted() {
    this.validateAndSetupAudio(true);
    this.validateAndSetupAudioDebounced = debounce(
      this.validateAndSetupAudio,
      500,
    );
  },
  methods: {
    async validateAndSetupAudio(isValid = false) {
      this.audio = {};
      if (this.link.includes("spotify")) {
        this.parseSpotifyLink(this.link);
      } else if (
        this.link.includes("youtube") ||
        this.link.includes("youtu.be")
      ) {
        this.parseYoutubeLink(this.link);
      } else {
        this.parseOtherLink(this.link, isValid);
      }
    },

    parseSpotifyLink(link) {
      link = link.split("?")[0];
      const id = link.substring(link.lastIndexOf("/") + 1);
      const keyLength = 22;
      if (!id || id.length !== keyLength) {
        return;
      }
      this.audio = {
        type: "spotify",
        url: `https://open.spotify.com/embed/track/${id}?utm_source=generator`,
      };
    },

    parseYoutubeLink(link) {
      const regExp =
        /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = link.match(regExp);
      const keyLength = 11;

      const id = match && match[2].length === keyLength ? match[2] : null;
      if (!id) {
        return;
      }

      this.audio = {
        type: "youtube",
        url: `https://www.youtube.com/embed/${id}?feature=oembed`,
      };
    },

    async parseOtherLink(link, isValid) {
      if (link) {
        // we want to call a debounced version of this method
        const valid = isValid || (await this.validateAudioLink(link));

        if (valid) {
          this.audio = {
            type: "embed",
            url: link,
            audioType: "audio/mpeg",
          };
        }
      }
    },

    async validateAudioLink(link) {
      try {
        const res = await fetch(link);
        const contentType = res.headers.get("Content-Type");
        return contentType.startsWith("audio");
      } catch (err) {
        return false;
      }
    },
  },
};
</script>

<style>
.audio-wrapper {
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
}

.audio-wrapper:has(svg.placeholder-image) {
  background-color: #f2f4f7;
  height: 250px;
  border-radius: 4px;
}
</style>
